;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import mitt, { type Emitter } from "mitt";
import type { GoalsConfig } from "@/components/shared/performance/goals/types";
import type { GoalPostType, ProviderType } from "@/prisma/generated/client";
import type { InvitedUser, WorkspaceMember } from "@/types/user";
import type { View } from "@/types/workspace";

export type ModalEventState = {
	state: "on" | "off";
	/** When true, the surface was opened by an onboarding tour step action. */
	tourOrigin?: boolean;
	/**
	 * Set when the tour closes a host while navigating (Back leaving an overlay
	 * group). Hosts should not treat this as a user abandon of the tour.
	 */
	reason?: "tour-navigation";
};

export type SettingsDataProviderEmitterState = ModalEventState & {
	data: {
		providerId: string | null;
	};
};

export type SettingsWorkspaceUsersEmitterState = ModalEventState & {
	data: {
		user: WorkspaceMember | null;
	};
};

export type ModalEvents = {
	"settings.workspace.data_provider.delete": SettingsDataProviderEmitterState;
	"settings.workspace.data_provider.edit": SettingsDataProviderEmitterState;
	"settings.workspace.data_provider.finish_setup": SettingsDataProviderEmitterState;
	"settings.workspace.data_provider.finish_podium_setup": SettingsDataProviderEmitterState;
	"settings.workspace.data_provider.create": ModalEventState & {
		data?: {
			preferredProvider?: ProviderType;
			/** When set, only these providers will be available for selection */
			restrictToProviders?: ProviderType[];
		};
	};
	"settings.workspace.data_provider.create_success": {
		providerType: ProviderType;
	};
	"settings.workspace.users.edit": SettingsWorkspaceUsersEmitterState;
	"settings.workspace.users.delete": SettingsWorkspaceUsersEmitterState;
	"settings.workspace.users.invite.create": ModalEventState;
	"settings.workspace.users.invite.delete": ModalEventState & {
		data: {
			user: InvitedUser | null;
		};
	};
	"settings.workspace.dashboards.create": ModalEventState;
	"settings.workspace.dashboards.delete": ModalEventState & {
		data: {
			viewId: string | null;
		};
	};
	"settings.workspace.dashboards.edit": ModalEventState & {
		data: {
			view: View | null;
		};
	};
	"settings.workspace.edit": ModalEventState;
	"settings.organization.edit": ModalEventState;
	"settings.organization.deactivate": ModalEventState;
	"settings.edit_profile"?: boolean;
	"leaderboard.configuration": ModalEventState;
	"share.view.dialog": ModalEventState;
};

export type CommentEvents = {
	comments: ModalEventState & {
		data: { viewVisualizationId: string | null; tab?: "comments" | "mentions" };
	};
};

export type AlertsEvents = {
	alerts: ModalEventState & {
		data: {
			viewVisualizationGroupId: string | null;
		};
	};
};

export type GoalsAndBenchmarkEvents = {
	performance: ModalEventState & {
		data: {
			tab: "goals" | "benchmarks";
			goalsConfig?: GoalsConfig;
			viewVisualizationId?: string;
			viewVisualizationGroupId?: string;
		};
	};
	"goals.create": ModalEventState & {
		data: {
			amount: number;
			metric?: string;
			id?: string;
			label?: string;
			type?: GoalPostType;
			startDate?: Date;
			endDate?: Date;
			goalsConfig: GoalsConfig;
			viewVisualizationId: string;
			viewVisualizationGroupId?: string;
		};
	};
};

export type WorkspaceEvents = {
	"workspace.create.new": ModalEventState;
};

export type AuthEvents = {
	"user.unauthorized": undefined;
};

export type TopLevelFilterEvents = Record<
	"top-level-filter",
	{
		key: string;
		label?: string;
		value: unknown;
	}
>;

export type ModalEventsKeys = keyof ModalEvents;

type Events = ModalEvents &
	CommentEvents &
	AuthEvents &
	WorkspaceEvents &
	GoalsAndBenchmarkEvents &
	AlertsEvents &
	TopLevelFilterEvents;

export const emitter: Emitter<Events> = mitt<Events>();
